import type { ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { EmptyState, Loading } from './EmptyState';
import type { IconName } from './Icon';

interface QueryStateProps<T> {
  query: UseQueryResult<T>;
  children: (data: T) => ReactNode;
  /** Custom loading node (e.g. a skeleton). Defaults to a spinner row. */
  skeleton?: ReactNode;
  /** When the resolved value is considered "empty". */
  isEmpty?: (data: T) => boolean;
  emptyIcon?: IconName;
  emptyTitle?: ReactNode;
  emptyHint?: ReactNode;
  emptyAction?: ReactNode;
  loadingLabel?: string;
}

/**
 * Standard async boundary for every data-driven screen: renders loading →
 * error → empty → content from a TanStack Query result. Use this instead of
 * reading `data ?? []` directly so loading/empty/error states are never skipped.
 */
export function QueryState<T>({
  query,
  children,
  skeleton,
  isEmpty,
  emptyIcon = 'stack',
  emptyTitle = 'Nothing here yet',
  emptyHint,
  emptyAction,
  loadingLabel,
}: QueryStateProps<T>) {
  if (query.isLoading) return <>{skeleton ?? <Loading label={loadingLabel} />}</>;

  if (query.isError) {
    return (
      <EmptyState
        icon="bell"
        title="Couldn't load this"
        hint={query.error instanceof Error ? query.error.message : 'Something went wrong. Please try again.'}
        action={
          <button className="btn btn-ghost btn-sm" onClick={() => query.refetch()}>
            Try again
          </button>
        }
      />
    );
  }

  const data = query.data as T;
  if (isEmpty && isEmpty(data)) {
    return <EmptyState icon={emptyIcon} title={emptyTitle} hint={emptyHint} action={emptyAction} />;
  }

  return <>{children(data)}</>;
}
